import { Actions, Entities } from '@space-core/datahub'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'

import useDefaultClassName from '@space-shell/common/hooks/useDefaultClassName'
import useRequest from '@space-shell/common/hooks/useRequest'

import { ViewAsEndpoints } from '@space-shell/common/services/viewAs/constants'
import ViewAsService from '@space-shell/common/services/viewAs/service'

import type { ClassResponse } from '@space-shell/common/services/viewAs/types'

import { useMembershipStore, useViewAsStore } from '@space-shell/core/store'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useSelectCurrentClassItem } from './useSelectCurrentClassItem'

import { ViewButton } from '../viewButton'

type ClassButtonProps = {
  className?: string
}

const ClassButton = ({ className }: ClassButtonProps) => {
  const { t } = useTranslation()
  const { productId } = useParams<{ productId: string }>()
  const { sendDataHubEvent } = useDataHubEvents()
  const { membershipSlug } = useMembershipStore()
  const { classItem } = useViewAsStore()
  const { getTranslatedName } = useDefaultClassName()

  const { data, isLoading, isError } = useRequest(
    [ViewAsEndpoints.get(membershipSlug, productId as string)],
    () => ViewAsService.getClasses(membershipSlug, productId as string)
  )

  useSelectCurrentClassItem(data)

  if (isLoading || isError || data?.length === 0) {
    return null
  }

  const handleItemClick = (classItem: ClassResponse, callback: () => void) => {
    useViewAsStore.setState({ classItem })
    callback()

    sendDataHubEvent({ group: Entities.HEADER_VIEW_AS, name: Actions.CLICK_VIEW_AS_CLASS })
  }

  return (
    <ViewButton
      className={className}
      fieldAction={{
        label: `${t('view_as.class.field_title')}:`,
        title: getTranslatedName(classItem?.name || '')
      }}
      modal={{
        title: t('view_as.class.title'),
        description: `${t('view_as.class.description')}:`
      }}>
      {({ onModalClose }) => {
        return data.map((item, index) => {
          const isLastItem = index === data.length - 1
          const title = getTranslatedName(item.name)

          return (
            <ViewButton.Item
              title={title}
              key={item.hash}
              className={clsx({ '_mb-4': !isLastItem })}
              isSelected={item.hash === classItem?.hash}
              onClick={() => handleItemClick(item, onModalClose)}
            />
          )
        })
      }}
    </ViewButton>
  )
}

export default ClassButton
